import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createTenantPersistConfig, PERSIST_STORE_KEYS } from './persist';

export type TargetMetricsSnapshot = {
  signalsCount: number;
  findingsCount: number;
  assetsCount?: number;
  executionsCount?: number;
  domainsInScope?: number;
  crownJewelCount?: number;
  ownedPrincipalCount?: number;
  onlineEnrollmentCount?: number;
  agentsCount?: number;
  componentsCount?: number;
};

type TargetVisits = Record<string, number>;
type TargetMetricsHistory = Record<string, TargetMetricsSnapshot>;
type AssetsVisits = Record<string, number>;
type SessionBaselines = Record<string, number | null>;
type SessionSnapshots = Record<string, TargetMetricsSnapshot | null>;

type TargetVisitsStore = {
  visits: TargetVisits;
  metricsHistory: TargetMetricsHistory;
  assetsVisits: AssetsVisits;
  assetsSessionBaselines: SessionBaselines;
  homeSessionBaselines: SessionBaselines;
  homeSessionSnapshots: SessionSnapshots;
  recordVisit: (scopeKey: string) => void;
  getLastVisit: (scopeKey: string) => Date | undefined;
  consumeAssetsVisitBaseline: (scopeKey: string) => number | null;
  consumeHomeVisitBaseline: (scopeKey: string) => number | null;
  saveMetricsSnapshot: (
    scopeKey: string,
    metrics: TargetMetricsSnapshot,
  ) => void;
  getMetricsSnapshot: (scopeKey: string) => TargetMetricsSnapshot | undefined;
  hasNewMetrics: (
    scopeKey: string,
    currentMetrics: TargetMetricsSnapshot,
  ) => boolean;
};

export const useTargetVisitsStore = create<TargetVisitsStore>()(
  persist(
    (set, get) => ({
      visits: {},
      metricsHistory: {},
      assetsVisits: {},
      assetsSessionBaselines: {},
      homeSessionBaselines: {},
      homeSessionSnapshots: {},

      recordVisit: (scopeKey) => {
        set((state) => ({
          visits: {
            ...state.visits,
            [scopeKey]: Date.now(),
          },
        }));
      },

      getLastVisit: (scopeKey) => {
        const timestamp = get().visits[scopeKey];

        return timestamp ? new Date(timestamp) : undefined;
      },

      consumeAssetsVisitBaseline: (scopeKey) => {
        const { assetsSessionBaselines, assetsVisits } = get();
        const sessionBaseline = assetsSessionBaselines[scopeKey];

        if (sessionBaseline !== undefined) {
          return sessionBaseline;
        }

        const previous = assetsVisits[scopeKey] ?? null;

        set({
          assetsSessionBaselines: {
            ...assetsSessionBaselines,
            [scopeKey]: previous,
          },
          assetsVisits: {
            ...assetsVisits,
            [scopeKey]: Date.now(),
          },
        });

        return previous;
      },

      consumeHomeVisitBaseline: (scopeKey) => {
        const {
          homeSessionBaselines,
          homeSessionSnapshots,
          metricsHistory,
          visits,
        } = get();
        const sessionBaseline = homeSessionBaselines[scopeKey];

        if (sessionBaseline !== undefined) {
          return sessionBaseline;
        }

        const previous = visits[scopeKey] ?? null;

        set({
          homeSessionBaselines: {
            ...homeSessionBaselines,
            [scopeKey]: previous,
          },
          homeSessionSnapshots: {
            ...homeSessionSnapshots,
            [scopeKey]: metricsHistory[scopeKey] ?? null,
          },
          visits: {
            ...visits,
            [scopeKey]: Date.now(),
          },
        });

        return previous;
      },

      saveMetricsSnapshot: (scopeKey, metrics) => {
        set((state) => ({
          metricsHistory: {
            ...state.metricsHistory,
            [scopeKey]: metrics,
          },
        }));
      },

      getMetricsSnapshot: (scopeKey) => {
        return get().metricsHistory[scopeKey];
      },

      hasNewMetrics: (scopeKey, currentMetrics) => {
        const previousMetrics = get().metricsHistory[scopeKey];

        if (!previousMetrics) {
          return false;
        }

        return (
          currentMetrics.signalsCount > previousMetrics.signalsCount ||
          currentMetrics.findingsCount > previousMetrics.findingsCount ||
          (currentMetrics.assetsCount ?? 0) >
            (previousMetrics.assetsCount ?? 0) ||
          (currentMetrics.executionsCount ?? 0) >
            (previousMetrics.executionsCount ?? 0) ||
          (currentMetrics.domainsInScope ?? 0) >
            (previousMetrics.domainsInScope ?? 0) ||
          (currentMetrics.crownJewelCount ?? 0) >
            (previousMetrics.crownJewelCount ?? 0) ||
          (currentMetrics.ownedPrincipalCount ?? 0) >
            (previousMetrics.ownedPrincipalCount ?? 0) ||
          (currentMetrics.onlineEnrollmentCount ?? 0) >
            (previousMetrics.onlineEnrollmentCount ?? 0) ||
          (currentMetrics.agentsCount ?? 0) >
            (previousMetrics.agentsCount ?? 0) ||
          (currentMetrics.componentsCount ?? 0) >
            (previousMetrics.componentsCount ?? 0)
        );
      },
    }),
    {
      ...createTenantPersistConfig({
        storeKey: PERSIST_STORE_KEYS.targetVisits,
        storage: 'superjson',
      }),
      partialize: (state) => ({
        visits: state.visits,
        metricsHistory: state.metricsHistory,
        assetsVisits: state.assetsVisits,
      }),
    },
  ),
);
